import { listEntries, copyEntry } from '@/core/api/library/library';
import logger from '@/app/logger';

export const STARTERS_PATH = '/public/starters';

export interface Starter {
    id: string;
    name: string;
    title: string;
    description: string;
    image: string;
}

/** As returned by `api/library/ls` — camel-cased by AddNewtonsoftJson, except the remapped `_name`. */
interface PublicEntry {
    id: string;
    _name?: string;
    isFolder?: boolean;
}

/**
 * Presentation for each published starter, keyed by its folder name in the public library.
 * Hardcoded until we understand what authors actually need to say about a starter.
 */
const PRESENTATION: Record<string, { title: string; description: string; image: string }> = {
    tutorial: {
        title: 'Learn Mythic Table',
        description: 'A short guided campaign that walks you through maps, tokens and the chat.',
        image: '/static/assets/tutorial/campaign-banner.jpg',
    },
};

const FALLBACK_IMAGE = '/static/assets/campaigns/default.jpg';

function present(folderName: string): { title: string; description: string; image: string } {
    return (
        PRESENTATION[folderName] ?? {
            title: folderName,
            description: '',
            image: FALLBACK_IMAGE,
        }
    );
}

/**
 * Lists the starters available to copy. Returns an empty list rather than throwing when the
 * public library has none published, so campaign creation never depends on deployed content.
 */
export async function fetchStarters(): Promise<Starter[]> {
    try {
        const entries = (await listEntries(STARTERS_PATH, 'exclude', false)) as PublicEntry[] | null;
        const starters = (entries ?? [])
            .filter((entry) => entry.isFolder)
            .map((entry) => {
                const name = entry._name ?? entry.id;
                return { id: entry.id, name, ...present(name) };
            });
        logger.info(`${STARTERS_PATH}: ${entries?.length ?? 0} entries, ${starters.length} starter(s)`);
        return starters;
    } catch (error) {
        logger.warn(`Could not list starters at ${STARTERS_PATH}`, error);
        return [];
    }
}

/**
 * Copies what is *inside* the starter, not the starter folder, so the campaign opens on its content
 * instead of a folder the player has to look inside. Sequential because each copy resolves its own
 * name against the siblings already there.
 */
export async function copyStarterIntoCampaign(starter: Starter, campaignId: string): Promise<void> {
    const path = `${STARTERS_PATH}/${starter.name}`;
    const contents = (await listEntries(path, 'exclude', false)) as PublicEntry[] | null;
    logger.info(`${path}: copying ${contents?.length ?? 0} item(s) into campaign ${campaignId}`);
    for (const entry of contents ?? []) {
        await copyEntry(entry.id, `/campaigns/${campaignId}/`);
    }
}
